import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvatarBadge } from "@/components/AvatarBadge";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm";

const uploadFile = async (file, onProgress) => {
  const { data: up } = await http.post("/reels/uploads", { content_type: file.type, size: file.size });
  for (let i = 0; i < up.chunks; i += 1) {
    const part = file.slice(i * up.chunk_size, (i + 1) * up.chunk_size);
    await http.put(`/reels/uploads/${up.upload_id}/${i}`, part, { headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.round(((i + 1) / up.chunks) * 100));
  }
  return up.upload_id;
};

export const ReelComposer = ({ onPosted }) => {
  const { user } = useAuth();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [progress, setProgress] = useState(null);

  if (!user) {
    return (
      <div className="rounded-[24px] border border-purple-200/25 bg-[#170a24]/80 p-6 text-center" data-testid="reel-signin">
        <p className="font-display text-2xl">Share your own reels</p>
        <p className="mt-1 text-sm text-purple-100/85">Sign in to post pictures and short videos.</p>
        <button onClick={login} data-testid="reel-signin-btn" className="mt-4 rounded-full bg-purple-100 text-[#0e0616] px-6 py-2.5 text-sm font-medium hover:bg-amber-200 transition-colors">Sign in to post</button>
      </div>
    );
  }

  const choose = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const reset = () => { setFile(null); setPreview(null); setCaption(""); setProgress(null); if (input.current) input.current.value = ""; };
  const post = async () => {
    if (!file) return;
    setProgress(0);
    try {
      const uploadId = await uploadFile(file, setProgress);
      const { data } = await http.post("/reels", { upload_id: uploadId, caption });
      onPosted(data);
      reset();
      toast.success("Posted to the Hollow!");
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Upload failed. Please try again.");
      setProgress(null);
    }
  };

  return (
    <div className="rounded-[24px] border border-purple-200/25 bg-[#170a24]/80 p-5 sm:p-6" data-testid="reel-composer">
      <div className="flex items-center gap-3">
        <AvatarBadge avatar={user.avatar} size={36} />
        <p className="text-sm text-purple-100">Posting as <span className="text-white">{user.avatar?.label || user.name}</span></p>
      </div>
      <input ref={input} type="file" accept={ACCEPT} onChange={choose} className="hidden" data-testid="reel-file-input" />
      {!preview ? (
        <button onClick={() => input.current?.click()} data-testid="reel-pick-btn"
          className="mt-4 w-full rounded-2xl border border-dashed border-purple-200/40 py-10 flex flex-col items-center gap-2 text-purple-100 hover:border-amber-200/70 transition-colors">
          <ImagePlus className="w-7 h-7" />
          <span className="text-sm">Pick a picture or short video</span>
          <span className="text-xs text-purple-100/80">Pictures up to 10 MB, videos up to 50 MB</span>
        </button>
      ) : (
        <div className="mt-4 rounded-2xl overflow-hidden bg-black/40 aspect-[4/5] max-h-[420px] mx-auto" data-testid="reel-preview">
          {file?.type.startsWith("video") ? <video src={preview} controls playsInline className="w-full h-full object-contain" />
            : <img src={preview} alt="Preview" className="w-full h-full object-contain" />}
        </div>
      )}
      <textarea value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 300))} rows={2} placeholder="Write a caption..."
        data-testid="reel-caption-input"
        className="mt-4 w-full rounded-xl border border-purple-200/30 bg-black/30 px-4 py-2.5 text-sm text-purple-50 placeholder:text-purple-200/60 focus:outline-none focus:border-amber-200/60" />
      <div className="mt-3 flex items-center justify-end gap-3">
        {file && progress === null && <button onClick={reset} data-testid="reel-cancel-btn" className="text-sm text-purple-100 hover:text-white">Cancel</button>}
        <button onClick={post} disabled={!file || progress !== null} data-testid="reel-post-btn"
          className="inline-flex items-center gap-2 rounded-full bg-purple-100 text-[#0e0616] px-6 py-2.5 text-sm font-medium hover:bg-amber-200 transition-colors disabled:opacity-50">
          {progress !== null && <Loader2 className="w-4 h-4 animate-spin" />}
          {progress !== null ? `Uploading ${progress}%` : "Post"}
        </button>
      </div>
    </div>
  );
};
